import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Spin, Result, Button, Typography } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
const { Paragraph, Text } = Typography;

// Define the shape of the context
interface LoadingContextProps {
  isLoading: boolean;
  success: SuccessData | null;
  errors: string[] | null;
  setLoading: (state: boolean) => void;
  setSuccess: (data: SuccessData | null) => void;
  setErrors: (messages: string[] | null) => void;
  reset: () => void;
}

// Define the shape of Success Data
interface SuccessData {
  title: string;
  subTitle?: string;
  extraButtons?: ReactNode[];
}

// Create context with a default value
const LoadingContext = createContext<LoadingContextProps>({
  isLoading: false,
  success: null,
  errors: null,
  setLoading: () => {},
  setSuccess: () => {},
  setErrors: () => {},
  reset: () => {}
});

// Custom hook to use the context
export const useLoading = () => useContext(LoadingContext);

// Provider component
interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<SuccessData | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);

  const reset = () => {
    setLoading(false);
    setSuccess(null);
    setErrors(null);
  };

  const customReset = (buttonClick) => {
    reset();
    buttonClick()
  };

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        success,
        errors,
        setLoading,
        setSuccess,
        setErrors,
        reset
      }}
    >
      {isLoading && <LoadingSpinner />}
      {success && <SuccessResult success={success} onReset={reset} customReset={customReset}/>}
      {errors && <ErrorResult errors={errors} onReset={reset} />}
      {children}
    </LoadingContext.Provider>
  );
};

// Loading Spinner Component
const LoadingSpinner: React.FC = () => (
  <div style={spinnerStyles}>
    <Spin tip="Loading..." size="large" />
  </div>
);

// Success Result Component
interface SuccessResultProps {
  success: SuccessData;
  onReset: () => void;
  customReset: (click) => void;
}

const SuccessResult: React.FC<SuccessResultProps> = ({ success, onReset, customReset }) => {
  const clonedButtons = success.extraButtons?.map((button, index) => {
    if (React.isValidElement(button)) {
      // Check if the element already has an onClick prop
      const existingOnClick = button.props.onClick;
      return React.cloneElement(button, {
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          if (existingOnClick) {
            customReset(existingOnClick(event));
          }
          onReset();
        }
      });
    }
    return button;
  }) || [
    <Button type="primary" key="reset" onClick={onReset}>
      Continue
    </Button>
  ];

  return (
  <div style={resultStyles}>
    <Result
      status="success"
      title={success.title}
      subTitle={success.subTitle}
      extra={[
        ...(clonedButtons),
      ]}
      style={resultModalStyles}
    />
  </div>);
};

// Error Result Component
interface ErrorResultProps {
  errors: string[];
  onReset: () => void;
}

const ErrorResult: React.FC<ErrorResultProps> = ({ errors, onReset }) => (
  <div style={resultStyles}>
    <Result
      status="error"
      title="Lo sentimos, algo no salio como lo esperabamos."
      style={resultModalStyles}
    >
    <div className="desc">
      <Paragraph>
        <Text
          strong
          style={{
            fontSize: 16,
          }}
        >
          Encontramos los siguientes errors:
        </Text>
      </Paragraph>

      {errors.map((error, index) => (
        <Paragraph key={index}>
          <CloseCircleOutlined className="site-result-demo-error-icon" /> {error}
        </Paragraph>
       ))}

      <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width:'100%'
      }}
      >
        <Button type="primary" key="retry" onClick={onReset}>
          Continuar
        </Button>
      </div>
    </div>
  </Result>
  </div>
);

// Styles for the spinner and result components
const spinnerStyles: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 1000
};

const resultStyles: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 1000
};


const resultModalStyles: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '1em',
  borderStyle: 'solid',
  borderColor: 'purple',
  borderWidth: '.2px'
};
